import React, { useCallback, useRef } from 'react';
import { CSSProperties } from '@linaria/react';
import { cx } from '@linaria/core';
import Select, { ActionMeta, OnChangeValue, components, DropdownIndicatorProps, NoticeProps, OptionProps, SingleValueProps, PlaceholderProps, ClearIndicatorProps, createFilter } from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { useTruncateLine } from 'components/TruncatedText/hooks/useTruncatedText';
import { copyToClipboard } from 'utils/copyToClipboard';
import { MenuPortalProps } from 'react-select/dist/declarations/src/components/Menu';
import { useRouter } from 'next/router';
import { GroupVersionValue, OptionVersionValue, ResourceType } from './usePlatformResourceVersionSelector';
import { Arrow } from './Arrow';
import { CircleLoader } from './CircleLoader';
import { WarningIcon } from './WarningIcon';
import { LensIcon } from './LensIcon';
import { CheckedIcon } from './CheckedIcon';
import { CopyIcon } from './CopyIcon';
import { LinkIcon } from './LinkIcon';
import { controlIsOpenStyled, controlStyled, disabledStyle, dropdownIndicatorStyled, valueContainerStyled, groupStyled, loadingMessageStyled, menuListStyled, menuPortalStyled, noOptionMessageStyled, optionHoverStyled, optionSelectedStyled, optionStyled, placeholderStyled, singleValueStyled } from './PlatformResourceVersionSelector.styled';
import { RVSTooltip } from './RVSTooltip';
const MenuPortal = (props: MenuPortalProps<OptionVersionValue, false, GroupVersionValue>) => {
  return <components.MenuPortal {...props}>{props.children}</components.MenuPortal>;
};
const DropdownIndicator = (props: DropdownIndicatorProps<OptionVersionValue, false, GroupVersionValue>) => {
  return <components.DropdownIndicator {...props}>
      <Arrow direction={props.selectProps.menuIsOpen ? 'up' : 'down'} />
    </components.DropdownIndicator>;
};
const IndicatorSeparator = () => null;
const LoadingIndicator = () => null;
const LoadingMessage = () => <div className={loadingMessageStyled}>
    <CircleLoader />
  </div>;
const NoOptionsMessage = ({
  selectProps
}: NoticeProps<OptionVersionValue, false, GroupVersionValue>) => {
  return <div className={noOptionMessageStyled}>
      {selectProps.hasError ? <WarningIcon /> : <LensIcon />}
      <h2>{selectProps.hasError ? 'There was an error loading the list of versions' : 'No version found'}</h2>
      <span>{selectProps.hasError ? 'Please refresh the page and try again' : `Try pasting in the ID of the version you're looking for!`}</span>
    </div>;
};
const DescriptionClamper = React.memo(({
  description
}: {
  description?: string;
}) => {
  const pRef = useRef<HTMLParagraphElement>(null);
  useTruncateLine(pRef, 2);
  if (!description) return null;
  return <p ref={pRef} className="description">
      {description}
    </p>;
});
const Option = (props: OptionProps<OptionVersionValue, false, GroupVersionValue>) => {
  const router = useRouter();
  const firstFiveLabel = props.data.label.slice(0, 5);
  const remainingLabel = props.data.label.slice(5);
  return <components.Option {...props} className={cx(optionStyled, props.isFocused && optionHoverStyled, props.isSelected && optionSelectedStyled)}>
      <p className="label">
        <span className="bold">{firstFiveLabel}</span>
        {remainingLabel}
      </p>

      <DescriptionClamper description={props.data.description || ''} />
      <p className="date">{props.data.date}</p>

      <div style={{
      width: '1.25rem',
      height: '1.25rem',
      gridArea: 'actionIcons'
    }}>
        {props.isSelected && <CheckedIcon />}
        {props.isFocused && props.data.link && <RVSTooltip text="Open version details">
            <div aria-hidden="true" onClick={props.data.link ? () => router.push(`${props.data.link}`) : undefined} style={{
          width: '20px',
          height: '20px'
        }}>
              <LinkIcon />
            </div>
          </RVSTooltip>}
      </div>
    </components.Option>;
};

// this is the copy icon
// replacing the clear functionality with copy
// this avoid modifying selected value component
const ClearIndicator = (props: ClearIndicatorProps<OptionVersionValue, false, GroupVersionValue>) => {
  const {
    getStyles,
    innerProps,
    getValue,
    selectProps
  } = props;
  const {
    clearEnabled,
    copyIdEnabled
  } = selectProps;
  const onClickCopy = useCallback((evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    evt.stopPropagation();
    const selected = getValue();
    if (selected && selected[0]) copyToClipboard(selected[0].value)();
  }, [getValue]);
  const onMouseDownBlocked = useCallback((evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // this prevents mouse down normal behavior
    evt.stopPropagation();
  }, []);
  if (copyIdEnabled) return <RVSTooltip text="Copy version ID">
        <div {...innerProps} onMouseDown={onMouseDownBlocked} onPointerDown={onClickCopy} role="button" style={{
      ...(getStyles('clearIndicator', props) as CSSProperties),
      cursor: 'pointer',
      padding: '0'
    }} tabIndex={0}>
          <CopyIcon />
        </div>
      </RVSTooltip>;
  if (clearEnabled) return <components.ClearIndicator {...props} />;
  return null;
};
const SingleValue = (props: SingleValueProps<OptionVersionValue, false, GroupVersionValue>) => {
  const firstFiveLabel = props.data.label.slice(0, 5);
  const remainingLabel = props.data.label.slice(5);
  return <components.SingleValue {...props} className={singleValueStyled}>
      <p className="label" title={`${firstFiveLabel}${remainingLabel}`}>
        <span className="bold">{firstFiveLabel}</span>
        {remainingLabel}
      </p>
      <span className="date">{props.data.date}</span>
    </components.SingleValue>;
};
const Placeholder = (props: PlaceholderProps<OptionVersionValue, false, GroupVersionValue>) => {
  const {
    options
  } = props;
  return <components.Placeholder {...props} className={placeholderStyled}>
      {!options.length ? 'No versions available' : props.children}
    </components.Placeholder>;
};
const customFilterOption = createFilter({
  ignoreCase: true,
  ignoreAccents: true,
  matchFrom: 'any',
  stringify: (option: FilterOptionOption<OptionVersionValue>) => `${option.label} ${option.data.description}`,
  trim: true
});
export interface PlatformResourceVersionSelectorProps {
  copyIdEnabled?: boolean;
  hasError?: boolean;
  clearEnabled?: boolean;
  isDisabled?: boolean;
  isLoading: boolean;
  onChange?: (value: OnChangeValue<OptionVersionValue, false>, actionMeta: ActionMeta<OptionVersionValue>) => void;
  options: OptionVersionValue[];
  placeholder?: string;
  type: ResourceType;
  value?: OptionVersionValue;
  className?: string;
}
export const PlatformResourceVersionSelector = ({
  copyIdEnabled = false,
  hasError,
  clearEnabled = false,
  isDisabled,
  isLoading,
  onChange,
  options,
  placeholder = isDisabled ? 'No versions available' : 'Search versions',
  type,
  value,
  className
}: PlatformResourceVersionSelectorProps) => <Select className={className} type={type} value={value || null} options={options} isClearable={clearEnabled || copyIdEnabled} hasError={hasError} minMenuHeight={400} menuPlacement="auto" isLoading={isLoading} isDisabled={!isLoading && (!options.length || isDisabled)} placeholder={placeholder} components={{
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
  LoadingIndicator,
  LoadingMessage,
  MenuPortal,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue
}} classNames={{
  control: state => cx(state.isFocused ? controlIsOpenStyled : controlStyled, state.isDisabled && disabledStyle),
  dropdownIndicator: () => dropdownIndicatorStyled,
  valueContainer: () => valueContainerStyled,
  singleValue: () => singleValueStyled,
  group: () => groupStyled,
  menuList: () => menuListStyled,
  menuPortal: () => menuPortalStyled,
  placeholder: () => placeholderStyled
}} menuPortalTarget={typeof document !== 'undefined' ? document.body : null} onChange={onChange} filterOption={customFilterOption} copyIdEnabled={copyIdEnabled} clearEnabled={clearEnabled} />;